import type { PracticeCase } from './learning-practice';

// These proposals belong to the fresh cases, not the live mission repository.
// Include useful work as well as mistakes: reflexive rejection is not review.
const proposals: Record<number, NonNullable<PracticeCase['proposal']>> = {
  0: {
    code: "printf 'closed\\n' > status.txt", decision: 'revise',
    reason: {
      en: 'This writes the answer you expect into the file. It does not inspect what the earlier commands left behind. Use cat status.txt to read the existing state.',
      ar: 'هذا يكتب الإجابة المتوقعة في الملف، ولا يفحص ما تركته الأوامر السابقة. استخدم cat status.txt لقراءة الحالة الموجودة.',
      de: 'Das schreibt die erwartete Antwort in die Datei. Es prüft nicht den Zustand nach den vorherigen Befehlen. Lies den vorhandenen Inhalt mit cat status.txt.',
      ja: 'これは期待する答えをファイルに書き込みます。前のコマンドが残した状態の確認にはなりません。cat status.txt で現在の内容を読んでください。',
    },
  },
  2: {
    code: 'git show HEAD:score.txt', decision: 'accept',
    reason: {
      en: 'This reads the committed copy without editing it. The second git add captured the new value; reading only the working file would not establish what the commit saved.',
      ar: 'هذا يقرأ النسخة المحفوظة في الالتزام دون تعديلها. التقط git add الثاني القيمة الجديدة؛ قراءة ملف العمل وحده لا تثبت ما حُفظ في الالتزام.',
      de: 'Das liest die gespeicherte Kopie, ohne sie zu ändern. Das zweite git add hat den neuen Wert übernommen. Die Arbeitsdatei allein belegt den Inhalt des Commits nicht.',
      ja: 'これはコミット内のコピーを変更せずに読みます。2回目の git add が新しい値を取り込みました。作業中のファイルだけではコミット内容を確認できません。',
    },
  },
  3: {
    code: 'assert.ok(total)', decision: 'revise',
    reason: {
      en: 'A function object is truthy even when it returns the wrong value. Call total with the stated input and compare the returned value with the requirement.',
      ar: 'كائن الدالة يحقق هذا التأكيد حتى لو أعاد قيمة خاطئة. استدعِ total بالمدخل المحدد وقارن القيمة المعادة بالمتطلب.',
      de: 'Ein Funktionsobjekt ist truthy, selbst wenn es einen falschen Wert zurückgibt. Rufe total mit der angegebenen Eingabe auf und vergleiche den Rückgabewert mit der Anforderung.',
      ja: '関数オブジェクトは戻り値が間違っていても truthy です。指定された入力で total を呼び、戻り値を要件と比較してください。',
    },
  },
  9: {
    code: 'return total + 5 - 2;', decision: 'accept',
    reason: {
      en: 'This preserves both the fee and the discount. Verify the combined result; a test of just one branch cannot show that both requirements survived.',
      ar: 'هذا يحتفظ بالرسوم والخصم معًا. تحقّق من الناتج المشترك؛ اختبار جانب واحد لا يثبت بقاء المتطلبين.',
      de: 'Das erhält Gebühr und Rabatt. Prüfe das gemeinsame Ergebnis; ein Test nur einer Seite belegt nicht beide Anforderungen.',
      ja: 'これは手数料と割引を両方保ちます。片方だけのテストでは両方の要件を確認できないので、組み合わせた結果を調べてください。',
    },
  },
  10: {
    code: 'git reset --hard HEAD@{0}', decision: 'revise',
    reason: {
      en: 'HEAD@{0} is the position after the reset, not the lost commit. This also changes state before inspecting the candidate. Inspect bb22222 with git show first.',
      ar: 'HEAD@{0} هو الموضع بعد إعادة الضبط، وليس الالتزام المفقود. وهذا يغيّر الحالة قبل فحص المرشح. افحص bb22222 باستخدام git show أولًا.',
      de: 'HEAD@{0} ist die Position nach dem Reset, nicht der verlorene Commit. Außerdem verändert das den Zustand vor der Prüfung. Untersuche bb22222 zuerst mit git show.',
      ja: 'HEAD@{0} はリセット後の位置で、失われたコミットではありません。候補を調べる前に状態も変更します。まず git show で bb22222 を確認してください。',
    },
  },
  13: {
    code: 'assert.equal(price(35), 30)', decision: 'accept',
    reason: {
      en: 'This checks the exact threshold and the required discount. It would catch a change that incorrectly excluded equality. Also consider a below-threshold input before trusting the whole function.',
      ar: 'هذا يفحص الحد نفسه والخصم المطلوب، ويكشف تغييرًا يستبعد المساواة خطأً. افحص أيضًا مدخلًا أقل من الحد قبل الوثوق بالدالة كاملة.',
      de: 'Das prüft die genaue Grenze und den geforderten Rabatt. Es erkennt eine Änderung, die Gleichheit ausschließt. Prüfe auch eine Eingabe unter der Grenze, bevor du der ganzen Funktion vertraust.',
      ja: 'これは境界そのものと必要な割引を確認し、等しい場合を除外する変更を検出できます。関数全体を信頼する前に、境界未満の入力も考えてください。',
    },
  },
  14: {
    code: 'assert.ok(total)', decision: 'revise',
    reason: {
      en: 'This does not call either function. It would pass even if total stopped adding three. Check the returned value of the complete outer call.',
      ar: 'هذا لا يستدعي أيًا من الدالتين، وسينجح حتى لو توقفت total عن إضافة ثلاثة. افحص القيمة المعادة من الاستدعاء الخارجي كاملًا.',
      de: 'Das ruft keine der Funktionen auf. Es besteht auch, wenn total keine drei mehr addiert. Prüfe den Rückgabewert des vollständigen äußeren Aufrufs.',
      ja: 'これはどちらの関数も呼びません。total が3を加えなくなっても成功します。外側の呼び出し全体の戻り値を確認してください。',
    },
  },
  15: {
    code: 'assert.equal(points(30), 4)', decision: 'accept',
    reason: {
      en: 'A failing test here exposes the bug: the requirement includes equality but the implementation excludes it. Keep the requirement-based assertion, fix the condition, and show this test going from red to green.',
      ar: 'فشل الاختبار هنا يكشف الخطأ: المتطلب يشمل المساواة والتنفيذ يستبعدها. احتفظ بالتأكيد المبني على المتطلب، وأصلح الشرط، وأظهر انتقال الاختبار من الفشل إلى النجاح.',
      de: 'Ein scheiternder Test deckt hier den Fehler auf: Die Anforderung schließt Gleichheit ein, der Code nicht. Behalte die Assertion, korrigiere die Bedingung und zeige den Wechsel von Rot zu Grün.',
      ja: 'ここでのテスト失敗は、要件が含む等しい場合を実装が除外している不具合を示します。要件に基づくアサーションを保ち、条件を直して失敗から成功への変化を示してください。',
    },
  },
};

export const practiceProposal = (level: number) => proposals[level];
